import { useCallback, useState, useMemo } from 'react';

import { AddCircleOutline, KeyboardArrowLeft } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Typography,
  Divider,
  FormHelperText,
  Grid,
  TextField,
} from '@mui/material';
import { AxiosError } from 'axios';
import { useHistory, Prompt } from 'react-router-dom';

import { deleteValidatedWorkClaim, patchCandidateStatus } from 'api';
import { LinkButton } from 'components';
import { AlertModal } from 'components/AlertModal';
import { InfoCell } from 'components/InfoCell';
import { timing } from 'config/theme';
import {
  wasNotEmployed,
  booleanOptionsArray,
  notEmployedDetailOptionsArray,
  getErrorDetail,
} from 'helpers';
import {
  convertBoolToString,
  convertToDateShort,
  convertStringToBool,
} from 'helpers/formatHelper';
import { useCandidateContext } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import {
  EmployerFullCandidateProps,
  ReviewStatus,
  ValidatedWorkClaimProps,
} from 'types';

import { Sticky } from '../../styledComponents';
import { Reference } from '../Reference';
import {
  InfoCellColumn,
  InfoCellRow,
  StyledRadioButtonGroup,
  CandidateContractWrapper,
} from '../styledComponents';
import { ValidatedWorkPeriod } from '../ValidatedWorkPeriod';
import { makeNewWorkPeriod } from './data';

export const CandidateValidatedWorkPeriods = (
  props: EmployerFullCandidateProps,
) => {
  const history = useHistory();
  const [candidate, setCandidate] = useState(props);
  const {
    firstname,
    lastname,
    birthdate,
    validated_work_claims,
    review_status,
    review_status_comment,
    passport_number,
    tazkera,
    work_claims,
    id,
  } = candidate;
  const tazkeras = useMemo(
    () =>
      (tazkera?.split(',') ?? []).map((val, index) => ({
        title: index === 0 ? 'Tazkera Number' : 'Alternative Tazkera Number',
        value: val,
        // Val should be unique
        key: val,
      })),
    [tazkera],
  );
  const passports = useMemo(
    () =>
      (passport_number?.split(',') ?? []).map((val, index) => ({
        title: index === 0 ? 'Passport Number' : 'Alternative Passport Number',
        value: val,
        // Val should be unique
        key: val,
      })),
    [passport_number],
  );
  const { updateCandidate, toggleSubmitted } = useCandidateContext();
  const [claimErrors, setClaimErrors] = useState(
    validated_work_claims.map(() => false),
  );
  const [employedAlert, setEmployedAlert] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [employed, setEmployed] = useState(
    wasNotEmployed(review_status) && validated_work_claims.length === 0
      ? false
      : validated_work_claims.length === 0
      ? null
      : true,
  );
  const [reviewStatusComment, setReviewStatusComment] = useState(
    review_status_comment,
  );

  const [errors, setErrors] = useState({
    employedSetError: false,
    missingDetailsError: false,
    employedNoClaimsError: false,
    reviewStatusCommentError: false,
  });
  const { setSnack } = useSnackContext();

  const disabled = useMemo(
    // disable saving if there are work claims that are unsaved or contain errors or if there are errors on the candidate form.
    () => {
      return (
        Object.values(errors).some((error) => error) ||
        Object.values(claimErrors).some((error) => error) ||
        validated_work_claims.some((claim) => claim.id === null)
      );
    },
    [errors, claimErrors, validated_work_claims],
  );

  const revertToPendingReview = useCallback(async () => {
    const new_review_status = 'pending_user_review';
    if (candidate.review_status !== new_review_status) {
      try {
        await patchCandidateStatus({
          candidate_id: candidate.id,
          data: { review_status: new_review_status },
        });
        setCandidate((candidate) => ({
          ...candidate,
          review_status: new_review_status,
        }));
        updateCandidate({
          id: candidate.id,
          review_status: new_review_status,
        });
      } catch (e) {
        setSnack((props) => ({
          ...props,
          severity: 'error',
          open: true,
          message: getErrorDetail({
            error: e,
            fallback: 'Failed To Update Status',
          }),
          error: e as Error,
        }));
      }
    }
  }, [candidate.id, candidate.review_status, setSnack, updateCandidate]);

  const handleDeleteAllWorkClaims = useCallback(
    async (validated_work_claims: Array<ValidatedWorkClaimProps>) => {
      try {
        const deleteRequestArray = validated_work_claims
          .filter(({ id }) => id !== null)
          .map(({ id }) => deleteValidatedWorkClaim(id as number));
        await Promise.all(deleteRequestArray);
        setCandidate((candidate) => ({
          ...candidate,
          validated_work_claims: [],
        }));
        await revertToPendingReview();
      } catch (e) {
        if ((e as AxiosError).response?.status === 403) {
          setSnack((props) => ({
            ...props,
            severity: 'warning',
            open: true,
            message: 'Cannot Delete Submitted Work Claims',
          }));
        } else {
          setSnack((props) => ({
            ...props,
            severity: 'error',
            open: true,
            message: getErrorDetail({
              error: e,
              fallback: 'Failed To Delete All Work Claims',
            }),
            error: e as Error,
          }));
        }
      } finally {
        setEmployedAlert(false);
      }
    },
    [revertToPendingReview, setSnack],
  );

  const handleWorkPeriodAdd = useCallback(() => {
    setCandidate((candidate) => ({
      ...candidate,
      validated_work_claims: candidate.validated_work_claims.concat(
        makeNewWorkPeriod(id),
      ),
    }));
    if (errors.employedNoClaimsError) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        employedNoClaimsError: false,
      }));
    }
    setClaimErrors((prevErrors) => prevErrors.concat(false));
  }, [id, errors.employedNoClaimsError]);

  const handleWorkPeriodRemove = useCallback(
    async (d: { index: number; validated_work_claim_id: number | null }) => {
      const { index, validated_work_claim_id } = d;
      setClaimErrors((prevErrors) => [
        ...prevErrors.slice(0, index),
        ...prevErrors.slice(index + 1),
      ]);

      if (validated_work_claim_id) {
        try {
          await deleteValidatedWorkClaim(validated_work_claim_id);

          setCandidate((prevFormData) => ({
            ...prevFormData,
            validated_work_claims: prevFormData.validated_work_claims.filter(
              (_, ind) => ind !== index,
            ),
          }));

          // when deleting the final workclaim review_status should be pending_user_review
          if (candidate.validated_work_claims.length === 1) {
            await revertToPendingReview();
          }
        } catch (e) {
          if ((e as AxiosError).response?.status === 403) {
            setSnack((props) => ({
              ...props,
              severity: 'warning',
              open: true,
              message: 'Cannot Delete A Submitted Work Claim',
            }));
          } else {
            setSnack((props) => ({
              ...props,
              severity: 'error',
              open: true,
              message: getErrorDetail({
                error: e,
                fallback: 'Failed To Remove Work Claim',
              }),
              error: e as Error,
            }));
          }
        }
      } else {
        setCandidate((prevFormData) => {
          return {
            ...prevFormData,
            validated_work_claims: prevFormData.validated_work_claims.filter(
              (_, ind) => ind !== index,
            ),
          };
        });
      }
    },
    [candidate.validated_work_claims.length, revertToPendingReview, setSnack],
  );

  const handleSubmit = useCallback(async () => {
    try {
      // Validation
      const employedSetError = employed === null;
      const employedNoClaimsError = employed
        ? employed && validated_work_claims.length === 0
        : false;
      const missingDetailsError = !employed && !wasNotEmployed(review_status);
      const reviewStatusCommentError =
        review_status === 'did_not_work_insufficient_info' &&
        !reviewStatusComment;
      if (
        employedSetError ||
        employedNoClaimsError ||
        missingDetailsError ||
        reviewStatusCommentError
      ) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          employedSetError,
          employedNoClaimsError,
          missingDetailsError,
          reviewStatusCommentError,
        }));
        return;
      }

      setSubmitting(true);
      // review_in_progress gets upgraded on submission
      const newReviewStatus =
        review_status === 'review_in_progress'
          ? 'reviewed_and_verified'
          : review_status;

      // This data apparently can come back as null. Seems like we
      //  should really just fix the endpoint to return the valid data
      //  but the code below attempts to do the best it can eithe way
      const { data } = await patchCandidateStatus({
        candidate_id: id,
        data: {
          review_status: newReviewStatus,
          review_status_comment: reviewStatusComment || undefined,
        },
      });

      setCandidate((candidate) => ({
        ...candidate,
        review_status: data?.review_status ?? newReviewStatus,
      }));
      updateCandidate({
        id: data?.candidate?.id ?? id,
        review_status: data?.review_status ?? newReviewStatus,
      });
      toggleSubmitted(); // begin animation; pause before redirecting
      setTimeout(
        () => history.push('/home/employer_dashboard/candidates'),
        timing.submitted.beg,
      );
    } catch (e) {
      setSnack((props) => ({
        ...props,
        severity: 'error',
        open: true,
        message: getErrorDetail({
          error: e,
          fallback: 'Failed To Submit Candidate',
        }),
        error: e as Error,
      }));
    } finally {
      setSubmitting(false);
    }
  }, [
    id,
    employed,
    validated_work_claims.length,
    reviewStatusComment,
    history,
    review_status,
    toggleSubmitted,
    updateCandidate,
    setSnack,
  ]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={1}>
        <Sticky>
          <LinkButton
            to='/home/employer_dashboard/candidates'
            startIcon={<KeyboardArrowLeft color='inherit' />}
            style={{ textAlign: 'left', lineHeight: 1.2, marginTop: '40px' }}
          >
            BACK TO CANDIDATES
          </LinkButton>
        </Sticky>
      </Grid>
      <Grid item xs={10}>
        <CandidateContractWrapper>
          <Typography variant='h4'>{`${firstname} ${lastname}`}</Typography>
          <Divider sx={{ mt: 4, mb: 2 }} />
          <Typography variant='h5'>
            Reported Biographical Information
          </Typography>
          <Typography variant='body1'>
            This information should be used as a guide in confirming the
            identity of the individual. Please note that{' '}
            <b>some of the information may not be accurate.</b>
          </Typography>
          <InfoCellRow>
            <InfoCellColumn>
              <InfoCell
                title='Date Of Birth (DD/MM/YYYY)'
                value={convertToDateShort(birthdate)}
              />
            </InfoCellColumn>
          </InfoCellRow>
          {/* 
            Here the requirement is that if there are only 1 tazkera 
            and passport then they should be on the same line. Otherwise 
            each grouping should be separate 
          */}
          {tazkeras.length === 1 && passports.length === 1 ? (
            <InfoCellRow>
              {tazkeras.map((d) => {
                const { title, value, key } = d;
                return (
                  <InfoCellColumn key={key}>
                    <InfoCell title={title} value={value} />
                  </InfoCellColumn>
                );
              })}
              {passports.map((d) => {
                const { title, value, key } = d;
                return (
                  <InfoCellColumn key={key}>
                    <InfoCell title={title} value={value} />
                  </InfoCellColumn>
                );
              })}
            </InfoCellRow>
          ) : (
            <>
              {tazkeras.length > 0 && (
                <InfoCellRow>
                  {tazkeras.map((d) => {
                    const { title, value, key } = d;
                    return (
                      <InfoCellColumn key={key}>
                        <InfoCell title={title} value={value} />
                      </InfoCellColumn>
                    );
                  })}
                </InfoCellRow>
              )}
              {passports.length > 0 && (
                <InfoCellRow>
                  {passports.map((d) => {
                    const { title, value, key } = d;
                    return (
                      <InfoCellColumn key={key}>
                        <InfoCell title={title} value={value} />
                      </InfoCellColumn>
                    );
                  })}
                </InfoCellRow>
              )}
            </>
          )}
          <Divider sx={{ mt: 4, mb: 2 }} />
          <Typography variant='h5'>References</Typography>
          <Typography variant='body1'>
            Shown here is the information provided by the Department of State
            database for periods worked by the individual{' '}
            <b>This information is provided as reference only</b>. We know the
            information you supply in the Work History cards may be different.
            That is not surprising and we rely on your updated information to
            process these cases.
          </Typography>
          <br />
          {work_claims?.map((work_claim) => (
            <Reference key={work_claim.id} {...work_claim} />
          ))}
          <Divider sx={{ mt: 4, mb: 2 }} />
          <Typography variant='h5'>
            Did this person work for your company on a DOD contract?
          </Typography>
          <StyledRadioButtonGroup
            disabled={review_status === 'reviewed_and_verified'}
            onChange={(e) => {
              const { value } = e.currentTarget;
              if (
                employed &&
                value === 'false' &&
                validated_work_claims.length > 0
              ) {
                setEmployedAlert(true);
              } else {
                setEmployed(convertStringToBool(value) || false);
              }

              if (
                errors.employedSetError ||
                errors.employedNoClaimsError ||
                errors.missingDetailsError
              ) {
                setErrors((prevErrors) => {
                  return {
                    ...prevErrors,
                    employedSetError: false,
                    employedNoClaimsError:
                      prevErrors.employedNoClaimsError && value !== 'false',
                    missingDetailsError:
                      prevErrors.missingDetailsError && value !== 'false',
                  };
                });
              }
            }}
            checkedValue={convertBoolToString(employed)}
            options={booleanOptionsArray}
            error={errors.employedSetError}
            helperText={
              errors.employedSetError
                ? 'Please indicate if the candidate worked for you company or not'
                : ''
            }
          />
          {employed === false && (
            <>
              <Typography variant='h5'>Are you sure?</Typography>
              <StyledRadioButtonGroup
                onChange={(e) => {
                  const { value } = e.currentTarget;
                  if (errors.missingDetailsError) {
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      missingDetailsError: false,
                    }));
                  }
                  setCandidate({
                    ...candidate,
                    review_status:
                      (notEmployedDetailOptionsArray.find(
                        (option) => option.value === value,
                      )?.value as ReviewStatus) || 'pending_user_review',
                  });
                }}
                checkedValue={review_status || ''}
                options={notEmployedDetailOptionsArray}
                error={errors.missingDetailsError}
                helperText={
                  errors.missingDetailsError
                    ? 'Please indicate your certainty about this candidates employment with your company'
                    : ''
                }
              />
              <Typography variant='h5'>Comments</Typography>
              <TextField
                multiline
                rows={5}
                value={reviewStatusComment || ''}
                onChange={({ target: { value } }) => {
                  if (errors.reviewStatusCommentError) {
                    setErrors((prev) => {
                      return { ...prev, reviewStatusCommentError: false };
                    });
                  }
                  setReviewStatusComment(value);
                }}
                helperText={
                  errors?.reviewStatusCommentError ? 'Comment is required' : ''
                }
                error={errors?.reviewStatusCommentError}
              />
            </>
          )}
          {employed && (
            <>
              <Typography variant='h5'>Work History</Typography>
              <Typography sx={{ mb: 2 }} variant='body1'>
                Please{' '}
                <b>fill out a work period card below for EACH work period</b>{' '}
                completed by the individual. Multiple Work Periods may be
                associated with the same contract number.
              </Typography>
              {validated_work_claims.map((validated_work_claim, index) => {
                return (
                  <ValidatedWorkPeriod
                    handleWorkPeriodRemove={handleWorkPeriodRemove}
                    key={validated_work_claim.id || `contract${index}`}
                    index={index}
                    review_status={review_status}
                    //if any validated work claim has a birthdate set that birthdate as default
                    {...{
                      ...validated_work_claim,
                      employer_provided_date_of_birth:
                        validated_work_claim.employer_provided_date_of_birth ||
                        validated_work_claims?.find(
                          (claim) => claim.employer_provided_date_of_birth,
                        )?.employer_provided_date_of_birth ||
                        null,
                    }}
                    open={Boolean(validated_work_claim.open)}
                    candidate_id={id}
                    setClaimErrors={setClaimErrors}
                    setCandidate={setCandidate}
                  />
                );
              })}
              {validated_work_claims.length > 0 && (
                <>
                  <Typography variant='h5' sx={{ mt: 3 }}>
                    Additional Work History
                  </Typography>
                  <Typography sx={{ mb: 1 }} variant='body1'>
                    If you have additional periods of work not captured above,
                    click below to add another period
                  </Typography>
                </>
              )}
              <Button
                variant='outlined'
                onClick={() => handleWorkPeriodAdd()}
                color={errors.employedNoClaimsError ? 'error' : 'primary'}
                startIcon={<AddCircleOutline />}
              >
                {validated_work_claims.length === 0
                  ? 'ADD WORK PERIOD'
                  : 'ADD ADDITIONAL WORK PERIOD'}
              </Button>
              {errors.employedNoClaimsError && (
                <FormHelperText error>
                  Please enter a Work Period
                </FormHelperText>
              )}
            </>
          )}
          <Divider sx={{ mt: 4, mb: 2 }} />
          <Typography variant='h5'>Is this information complete?</Typography>
          <Typography variant='body1'>
            Have you entered all the work periods your company has for this
            individual?
          </Typography>
          <LoadingButton
            loading={submitting}
            variant='contained'
            onClick={handleSubmit}
            disabled={disabled}
          >
            SUBMIT
          </LoadingButton>
          {disabled && (
            <FormHelperText error>
              There are errors or unsaved work periods on this form, please
              resolve them{' '}
            </FormHelperText>
          )}
        </CandidateContractWrapper>
        <AlertModal
          heading="Selecting 'No' now will delete any work periods for this individual."
          subHeading="This can't be undone"
          open={employedAlert}
        >
          <Button variant='outlined' onClick={() => setEmployedAlert(false)}>
            CANCEL
          </Button>
          <Button
            onClick={() => handleDeleteAllWorkClaims(validated_work_claims)}
          >
            PROCEED + DELETE
          </Button>
        </AlertModal>
        <Prompt
          when={disabled}
          message={() => 'Changes you made may not be saved.'}
        />
      </Grid>
      <Grid item xs={1}>
        <div />
      </Grid>
    </Grid>
  );
};
